/**
 * OuComplianceShell — new IA per docs/mocks/ou-compliance-redesign.html.
 *
 * Replaces the 5-tab `ComplianceGoogleOUPage` with a split-pane:
 *   rail (OU tree + footer nav) + main (one of 5 view modes)
 *
 * View routing is internal to the shell (hash-state) so we don't need new
 * top-level routes for every sub-view. The single route
 * `/compliance/google-ou` enters here; `/compliance/google-ou#ou=...`,
 * `#view=tenant`, `#view=audit-log`, `#view=settings`, `#view=compare`
 * select the sub-view.
 *
 * Salvaged from the legacy 4k-LOC page:
 *   - AuditDetail / DiffCard / JsonBlock         (OU Compliance + History tabs)
 *   - OuDeviceDrillDown                          (OU Devices tab uses the
 *                                                  same data shape)
 *   - TenantPoliciesTab                          (Tenant view)
 *   - SettingsTab                                (Settings view)
 *   - ReadOnlyBanner                             (header)
 *
 * Dissolved:
 *   - FleetTab (top-section becomes WorkspaceOverview, per-OU rows move to the rail)
 *   - AuditsTab Run dialog (Run moves to OU detail)
 *
 * Honest "unknown" state on Tenant cards is enforced by the salvaged
 * TenantPoliciesTab — see the legacy file for that logic. It already
 * surfaces awaiting-fetcher entries; we just present them with the new
 * dashed-circle ribbon by passing a styling prop.
 */

import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Paper,
  Button,
  Chip,
  CircularProgress,
  Alert,
  IconButton,
  Tooltip,
  TextField,
  InputAdornment,
  Stack,
  Divider,
  LinearProgress,
} from '@mui/material';
import {
  Refresh as RefreshIcon,
  PlayArrow as RunIcon,
  Search as SearchIcon,
  Launch as LaunchIcon,
  ArrowBack as BackIcon,
  Compare as CompareIcon,
  CheckCircle as CheckIcon,
  ErrorOutline as ErrorIcon,
  Warning as WarningIcon,
  HelpOutline as UnknownIcon,
  Devices as DevicesIcon,
  History as HistoryIcon,
} from '@mui/icons-material';
import {
  AuditDetail,
  DiffCard,
  JsonBlock,
  OuDeviceDrillDown,
  TenantPoliciesTab,
  SettingsTab,
  ReadOnlyBanner,
} from '../ComplianceGoogleOUPage';

// ── Design tokens (mirror docs/mocks/ou-compliance-redesign.html) ─────────
const TOKENS = {
  bg: '#f4f4f1',
  paper: '#ffffff',
  rail: '#14171c',
  rail2: '#1c2128',
  railText: '#c9ccd2',
  railTextDim: '#6d747f',
  railLine: '#262c35',
  ink: '#131418',
  ink2: '#2a2d35',
  inkMute: '#6b6f78',
  inkFade: '#a4a8b1',
  line: '#e7e6e1',
  hairline: '#dcdad3',
  ok: '#1f8a4a',
  warn: '#c48415',
  bad: '#d8351e',
  info: '#2d72c4',
  accent: '#c93b1d',
  accent2: '#9f3010',
  sans: "'Instrument Sans', system-ui, sans-serif",
  mono: "'JetBrains Mono', ui-monospace, monospace",
};

// ── OU tree node helpers ─────────────────────────────────────────────────

/** Build a hierarchical tree from a flat OU list. Returns root-children array. */
function buildOuTree(flatOus) {
  if (!Array.isArray(flatOus) || flatOus.length === 0) return [];
  const byPath = new Map();
  for (const o of flatOus) {
    byPath.set(o.path, { ...o, children: [] });
  }
  const roots = [];
  for (const o of flatOus) {
    const node = byPath.get(o.path);
    if (!o.parentPath) {
      roots.push(node);
      continue;
    }
    const parent = byPath.get(o.parentPath);
    if (parent) {
      parent.children.push(node);
    } else {
      // Orphaned (parent missing from listing); attach to root.
      roots.push(node);
    }
  }
  // Sort each level alphabetically by path.
  const sortRec = (nodes) => {
    nodes.sort((a, b) => a.path.localeCompare(b.path));
    for (const n of nodes) sortRec(n.children);
  };
  sortRec(roots);
  return roots;
}

/** Status dot derived from per-OU fleet row. 'unknown' when not audited. */
function ouStatusFromFleet(fleetRow) {
  if (!fleetRow) return 'unknown';
  if (fleetRow.status === 'compliant') return 'ok';
  if (fleetRow.status === 'drift' || fleetRow.status === 'missing') {
    const sevs =
      (fleetRow.summary && fleetRow.summary.severity) ||
      fleetRow.severity ||
      {};
    if ((sevs.critical || 0) > 0 || (sevs.high || 0) > 0) return 'bad';
    return 'warn';
  }
  if (fleetRow.status === 'failed') return 'bad';
  if (fleetRow.status === 'running') return 'warn';
  return 'unknown';
}

function StatusDot({ status, size = 8 }) {
  const colors = {
    ok: '#34c266',
    warn: '#f0b834',
    bad: '#f04a30',
    unknown: 'transparent',
    muted: '#5a606c',
  };
  const style = {
    width: size,
    height: size,
    borderRadius: '50%',
    flexShrink: 0,
    display: 'inline-block',
    background: colors[status] || colors.muted,
  };
  if (status === 'unknown') {
    style.border = `1px dashed ${colors.muted}`;
  }
  if (status === 'bad') {
    style.boxShadow = '0 0 0 3px rgba(240,74,48,0.22)';
  }
  return <span style={style} />;
}

// ── Shell ────────────────────────────────────────────────────────────────

export default function OuComplianceShell() {
  const navigate = useNavigate();
  const token =
    typeof window !== 'undefined' ? localStorage.getItem('token') : null;

  // Onboarding gate
  const [onboarding, setOnboarding] = useState({ status: 'loading' });

  useEffect(() => {
    if (!token) return;
    fetch('/api/google-ou-compliance/onboarding/status', {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then((r) => r.json())
      .then((d) => setOnboarding(d))
      .catch((e) =>
        setOnboarding({ status: 'error', error: e.message || String(e) }),
      );
  }, [token]);

  // View state synced to URL hash
  const [hash, setHash] = useState(
    typeof window !== 'undefined' ? window.location.hash : '',
  );
  useEffect(() => {
    const onHash = () => setHash(window.location.hash || '');
    window.addEventListener('hashchange', onHash);
    return () => window.removeEventListener('hashchange', onHash);
  }, []);

  const hashParams = useMemo(() => {
    const out = {};
    const raw = (hash || '').replace(/^#/, '');
    if (!raw) return out;
    for (const part of raw.split('&')) {
      const eq = part.indexOf('=');
      if (eq < 0) out[part] = true;
      else out[part.slice(0, eq)] = decodeURIComponent(part.slice(eq + 1));
    }
    return out;
  }, [hash]);

  const setView = useCallback((updates) => {
    const next = { ...hashParams, ...updates };
    Object.keys(next).forEach((k) => {
      if (next[k] === null || next[k] === undefined || next[k] === '') {
        delete next[k];
      }
    });
    const parts = Object.entries(next).map(
      ([k, v]) => `${k}=${encodeURIComponent(v)}`,
    );
    const newHash = parts.length ? `#${parts.join('&')}` : '';
    if (newHash !== window.location.hash) {
      window.location.hash = newHash;
    }
  }, [hashParams]);

  const selectedOuId = hashParams.ou || null;
  const viewMode = hashParams.view || (selectedOuId ? 'ou' : 'overview');
  const ouSubTab = hashParams.tab || 'compliance';
  const multiSelectMode = hashParams.multi === '1';
  const selectedOuIds = useMemo(() => {
    if (!hashParams.selected) return [];
    return String(hashParams.selected).split(',').filter(Boolean);
  }, [hashParams.selected]);

  // Fetch OU directory + per-OU assignments + per-OU latest audits
  const [ouState, setOuState] = useState({ loading: true, ous: [], error: null });
  const [assignmentsState, setAssignmentsState] = useState({
    loading: true,
    rows: [],
  });
  const [fleetByOu, setFleetByOu] = useState({ loading: true, byOu: {} });

  const refresh = useCallback(async () => {
    if (!token) return;
    setOuState((s) => ({ ...s, loading: true }));
    setFleetByOu({ loading: true, byOu: {} });
    setAssignmentsState({ loading: true, rows: [] });

    // OUs
    try {
      const resp = await fetch('/api/google-ou-compliance/ous', {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await resp.json();
      setOuState({
        loading: false,
        ous: data.data || [],
        error: null,
      });
    } catch (e) {
      setOuState({ loading: false, ous: [], error: e.message || String(e) });
    }

    // Assignments
    try {
      const resp = await fetch('/api/google-ou-compliance/ou-assignments', {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await resp.json();
      setAssignmentsState({ loading: false, rows: data.data || [] });
    } catch (e) {
      setAssignmentsState({ loading: false, rows: [], error: e.message });
    }

    // Fleet (latest per OU, by assignment)
    try {
      const resp = await fetch(
        '/api/google-ou-compliance/audits/fleet-by-assignment',
        { headers: { Authorization: `Bearer ${token}` } },
      );
      const data = await resp.json();
      const byOu = {};
      for (const r of data.data || []) {
        byOu[r.ouId] = r;
      }
      setFleetByOu({ loading: false, byOu });
    } catch (e) {
      setFleetByOu({ loading: false, byOu: {}, error: e.message });
    }
  }, [token]);

  useEffect(() => {
    if (onboarding.status === 'verified') {
      refresh();
    }
  }, [onboarding.status, refresh]);

  // Redirect on un-onboarded
  useEffect(() => {
    if (onboarding.status === 'no-tenant') {
      navigate('/onboarding/google-workspace');
    } else if (onboarding.status === 'unverified') {
      navigate('/onboarding/google-workspace?step=verify');
    }
  }, [onboarding.status, navigate]);

  if (!token) {
    return (
      <Box sx={{ p: 4 }}>
        <Alert severity="warning">Sign in to view OU compliance.</Alert>
      </Box>
    );
  }
  if (onboarding.status === 'loading') {
    return (
      <Box sx={{ p: 4, textAlign: 'center' }}>
        <CircularProgress />
      </Box>
    );
  }
  if (onboarding.status === 'error') {
    return (
      <Box sx={{ p: 4 }}>
        <Alert severity="error">
          Onboarding status failed: {onboarding.error}
        </Alert>
      </Box>
    );
  }
  if (onboarding.status === 'no-tenant' || onboarding.status === 'unverified') {
    // Already navigated; show a placeholder while the redirect lands.
    return (
      <Box sx={{ p: 4, textAlign: 'center' }}>
        <CircularProgress />
      </Box>
    );
  }

  const tenant = onboarding.tenant || {};
  const selectedOu = (ouState.ous || []).find((o) => o.id === selectedOuId);

  return (
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: '264px 1fr',
        minHeight: 'calc(100vh - 64px)',
        background: TOKENS.paper,
        fontFamily: TOKENS.sans,
        color: TOKENS.ink,
        borderRadius: 1,
        overflow: 'hidden',
        border: `1px solid ${TOKENS.hairline}`,
      }}
    >
      <OuTreeRail
        tenant={tenant}
        ous={ouState.ous}
        loading={ouState.loading}
        fleetByOu={fleetByOu.byOu}
        selectedOuId={selectedOuId}
        selectedOuIds={selectedOuIds}
        multiSelectMode={multiSelectMode}
        onSelectOu={(o) => {
          setView({ ou: o.id, view: 'ou', tab: 'compliance' });
        }}
        onToggleOu={(o) => {
          const next = new Set(selectedOuIds);
          if (next.has(o.id)) next.delete(o.id);
          else next.add(o.id);
          setView({
            selected: Array.from(next).join(','),
            view: next.size > 0 ? 'compare' : 'overview',
            ou: '',
            multi: '1',
          });
        }}
        onToggleMultiSelect={() => {
          setView({
            multi: multiSelectMode ? '' : '1',
            selected: multiSelectMode ? '' : hashParams.selected,
          });
        }}
        viewMode={viewMode}
        onSelectView={(v) => {
          if (v === 'overview') setView({ view: 'overview', ou: '' });
          else setView({ view: v, ou: '' });
        }}
      />

      <Box
        component="section"
        sx={{ background: TOKENS.paper, overflow: 'hidden' }}
      >
        <ReadOnlyBanner />
        {viewMode === 'overview' && (
          <WorkspaceOverview
            tenant={tenant}
            token={token}
            ous={ouState.ous}
            fleetByOu={fleetByOu.byOu}
            onOpenOu={(ouId) => setView({ ou: ouId, view: 'ou', tab: 'compliance' })}
            onRefresh={refresh}
          />
        )}
        {viewMode === 'ou' && selectedOu && (
          <OuDetailPage
            token={token}
            tenant={tenant}
            ou={selectedOu}
            fleetRow={fleetByOu.byOu[selectedOuId]}
            assignmentRow={(assignmentsState.rows || []).find(
              (a) => a.ouId === selectedOuId,
            )}
            subTab={ouSubTab}
            onSelectSubTab={(t) => setView({ tab: t })}
            onAuditDone={refresh}
          />
        )}
        {viewMode === 'ou' && !selectedOu && !ouState.loading && (
          <Box sx={{ p: 4 }}>
            <Alert severity="info">Select an OU in the rail to begin.</Alert>
          </Box>
        )}
        {viewMode === 'compare' && (
          <MultiOuCompare
            token={token}
            ous={ouState.ous}
            selectedIds={selectedOuIds}
            fleetByOu={fleetByOu.byOu}
            onClear={() => setView({ selected: '', view: 'overview', multi: '' })}
          />
        )}
        {viewMode === 'tenant' && (
          <Box sx={{ p: 3 }}>
            <PageHeader title="Tenant" emphasis="Policies" sub={`These audit tenant-wide Workspace configuration (SSO, 2SV, password, Drive sharing, MDM, etc.). Google scopes these at the customer level, so we run them once against the whole Workspace.`} />
            <Box sx={{ px: 1, pb: 4 }}>
              <TenantPoliciesTab token={token} onOpenAudit={() => {}} />
            </Box>
          </Box>
        )}
        {viewMode === 'audit-log' && (
          <AuditLogPage token={token} />
        )}
        {viewMode === 'settings' && (
          <Box sx={{ p: 3 }}>
            <PageHeader title="Settings" emphasis="" sub="Service Account + Domain-Wide Delegation status for the active tenant." />
            <Box sx={{ pb: 4 }}>
              <SettingsTab token={token} />
            </Box>
          </Box>
        )}
      </Box>
    </Box>
  );
}

// ── Page header (Instrument Sans, accent emphasis) ───────────────────────

function PageHeader({ title, emphasis, sub }) {
  return (
    <Box sx={{ px: 4, py: 3, borderBottom: `1px solid ${TOKENS.line}` }}>
      <Typography
        sx={{
          fontFamily: TOKENS.sans,
          fontWeight: 700,
          fontSize: 34,
          letterSpacing: '-0.025em',
          color: TOKENS.ink,
          lineHeight: 1.04,
          mb: 0.5,
        }}
      >
        {title}
        {emphasis && (
          <Box component="span" sx={{ color: TOKENS.accent, ml: 1 }}>
            {emphasis}
          </Box>
        )}
      </Typography>
      {sub && (
        <Typography
          sx={{ fontSize: 14, color: TOKENS.inkMute, maxWidth: 820 }}
        >
          {sub}
        </Typography>
      )}
    </Box>
  );
}

// ── Rail ─────────────────────────────────────────────────────────────────

function OuTreeRail({
  tenant,
  ous,
  loading,
  fleetByOu,
  selectedOuId,
  selectedOuIds,
  multiSelectMode,
  onSelectOu,
  onToggleOu,
  onToggleMultiSelect,
  viewMode,
  onSelectView,
}) {
  const [filter, setFilter] = useState('');
  const tree = useMemo(() => buildOuTree(ous), [ous]);

  // Track collapsed state per path
  const [collapsed, setCollapsed] = useState(() => new Set());
  const toggleCollapse = useCallback((path) => {
    setCollapsed((prev) => {
      const next = new Set(prev);
      if (next.has(path)) next.delete(path);
      else next.add(path);
      return next;
    });
  }, []);

  return (
    <Box
      sx={{
        background: TOKENS.rail,
        color: TOKENS.railText,
        display: 'flex',
        flexDirection: 'column',
        borderRight: `1px solid ${TOKENS.railLine}`,
        minHeight: '100%',
      }}
    >
      <Box
        sx={{
          px: 2.5,
          py: 2,
          borderBottom: `1px solid ${TOKENS.railLine}`,
          display: 'flex',
          alignItems: 'center',
          gap: 1.25,
        }}
      >
        <Box
          sx={{
            width: 22,
            height: 22,
            borderRadius: 1,
            background: `linear-gradient(135deg, ${TOKENS.accent}, ${TOKENS.accent2})`,
            display: 'grid',
            placeItems: 'center',
            color: '#fff',
            fontFamily: TOKENS.sans,
            fontWeight: 700,
            fontSize: 13,
          }}
        >
          a
        </Box>
        <Typography
          sx={{
            fontFamily: TOKENS.sans,
            fontWeight: 600,
            fontSize: 14,
            color: '#fff',
          }}
        >
          Automatrix{' '}
          <Box component="span" sx={{ color: TOKENS.railTextDim, fontWeight: 400 }}>
            · OU Compliance
          </Box>
        </Typography>
      </Box>

      <Box
        sx={{
          mx: 2,
          mt: 2,
          mb: 0.5,
          px: 1.5,
          py: 1.25,
          background: TOKENS.rail2,
          border: `1px solid ${TOKENS.railLine}`,
          borderRadius: 1,
        }}
      >
        <Typography
          sx={{
            fontFamily: TOKENS.mono,
            fontSize: 9,
            letterSpacing: '0.16em',
            textTransform: 'uppercase',
            color: TOKENS.railTextDim,
          }}
        >
          Workspace
        </Typography>
        <Typography
          sx={{ fontSize: 14, fontWeight: 500, color: '#fff', mt: 0.25 }}
        >
          {tenant.displayName || 'Loading…'}
        </Typography>
      </Box>

      <Box
        sx={{
          mx: 2,
          mt: 2.5,
          mb: 0.75,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}
      >
        <Typography
          sx={{
            fontFamily: TOKENS.mono,
            fontSize: 10,
            letterSpacing: '0.18em',
            textTransform: 'uppercase',
            color: TOKENS.railTextDim,
          }}
        >
          OU Tree · {ous?.length || 0}
        </Typography>
        <Box
          component="span"
          onClick={onToggleMultiSelect}
          sx={{
            fontSize: 11,
            color: multiSelectMode ? TOKENS.accent : '#e08750',
            cursor: 'pointer',
          }}
        >
          {multiSelectMode ? `${selectedOuIds.length} selected ✓` : 'Multi-select'}
        </Box>
      </Box>

      <Box sx={{ px: 1, pb: 0.5 }}>
        <TextField
          fullWidth
          size="small"
          placeholder="Filter OUs…"
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={{ fontSize: 14, color: TOKENS.railTextDim }} />
              </InputAdornment>
            ),
            sx: {
              fontSize: 12,
              color: '#fff',
              background: TOKENS.rail2,
              '& fieldset': { border: `1px solid ${TOKENS.railLine}` },
              '&:hover fieldset': { borderColor: `${TOKENS.railTextDim} !important` },
              '&.Mui-focused fieldset': { borderColor: `${TOKENS.accent} !important` },
            },
          }}
        />
      </Box>

      <Box sx={{ flex: 1, overflowY: 'auto', px: 1, py: 0.5, fontSize: 13 }}>
        {loading && (
          <Box sx={{ p: 1.5, color: TOKENS.railTextDim, fontSize: 12 }}>
            Loading OUs…
          </Box>
        )}
        {!loading && tree.length === 0 && (
          <Box sx={{ p: 1.5, color: TOKENS.railTextDim, fontSize: 12 }}>
            No OUs returned. Re-verify in Settings.
          </Box>
        )}
        {tree.map((node) => (
          <OuTreeNode
            key={node.path}
            node={node}
            indent={0}
            filter={filter}
            collapsed={collapsed}
            toggleCollapse={toggleCollapse}
            selectedOuId={selectedOuId}
            selectedOuIds={selectedOuIds}
            multiSelectMode={multiSelectMode}
            onSelectOu={onSelectOu}
            onToggleOu={onToggleOu}
            fleetByOu={fleetByOu}
          />
        ))}
      </Box>

      <Box
        sx={{
          px: 2,
          py: 1.5,
          borderTop: `1px solid ${TOKENS.railLine}`,
          display: 'flex',
          gap: 0.75,
          flexWrap: 'wrap',
        }}
      >
        {[
          { id: 'overview', label: 'Overview' },
          { id: 'tenant', label: 'Tenant' },
          { id: 'audit-log', label: 'Audit Log' },
          { id: 'settings', label: 'Settings' },
        ].map((it) => {
          const active = viewMode === it.id;
          return (
            <Box
              key={it.id}
              onClick={() => onSelectView(it.id)}
              sx={{
                fontSize: 12,
                color: active ? '#fff' : TOKENS.railTextDim,
                background: active ? TOKENS.rail2 : 'transparent',
                px: 1,
                py: 0.75,
                borderRadius: 0.5,
                cursor: 'pointer',
                '&:hover': { background: TOKENS.rail2, color: '#fff' },
              }}
            >
              {it.label}
            </Box>
          );
        })}
      </Box>
    </Box>
  );
}

function OuTreeNode({
  node,
  indent,
  filter,
  collapsed,
  toggleCollapse,
  selectedOuId,
  selectedOuIds,
  multiSelectMode,
  onSelectOu,
  onToggleOu,
  fleetByOu,
}) {
  const isCollapsed = collapsed.has(node.path);
  const hasChildren = node.children && node.children.length > 0;
  const fleetRow = fleetByOu[node.id];
  const status = ouStatusFromFleet(fleetRow);

  // Filter logic — node matches if its name/path includes filter OR any descendant does
  const matches = (n) => {
    if (!filter) return true;
    const f = filter.toLowerCase();
    if (n.name.toLowerCase().includes(f) || n.path.toLowerCase().includes(f))
      return true;
    return (n.children || []).some(matches);
  };
  if (!matches(node)) return null;

  const isSelected = selectedOuId === node.id;
  const isMultiSelected = selectedOuIds.includes(node.id);

  return (
    <>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          py: 0.625,
          px: 1.25,
          pl: 1.25 + indent * 2,
          borderRadius: 0.5,
          color: isSelected ? '#fff' : TOKENS.railText,
          background: isSelected || isMultiSelected ? TOKENS.rail2 : 'transparent',
          position: 'relative',
          cursor: 'pointer',
          '&:hover': { background: TOKENS.rail2 },
          ...(isSelected && {
            '&::before': {
              content: '""',
              position: 'absolute',
              left: 0,
              top: 6,
              bottom: 6,
              width: 2,
              background: TOKENS.accent,
            },
          }),
        }}
        onClick={() => (multiSelectMode ? onToggleOu(node) : onSelectOu(node))}
      >
        <Box
          component="span"
          onClick={(e) => {
            if (!hasChildren) return;
            e.stopPropagation();
            toggleCollapse(node.path);
          }}
          sx={{
            width: 12,
            color: TOKENS.railTextDim,
            fontSize: 9,
            cursor: hasChildren ? 'pointer' : 'default',
            userSelect: 'none',
          }}
        >
          {hasChildren ? (isCollapsed ? '▸' : '▾') : ''}
        </Box>
        {multiSelectMode && (
          <Box
            sx={{
              width: 12,
              height: 12,
              border: `1px solid ${TOKENS.railTextDim}`,
              borderRadius: 0.25,
              flexShrink: 0,
              background: isMultiSelected ? TOKENS.accent : 'transparent',
              borderColor: isMultiSelected ? TOKENS.accent : TOKENS.railTextDim,
              display: 'grid',
              placeItems: 'center',
              color: '#fff',
              fontSize: 8,
            }}
          >
            {isMultiSelected ? '✓' : ''}
          </Box>
        )}
        <StatusDot status={status} size={7} />
        <Box component="span" sx={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {node.name}
        </Box>
        {fleetRow && (
          <Box
            component="span"
            sx={{
              fontFamily: TOKENS.mono,
              fontSize: 10,
              color: isSelected ? TOKENS.railText : TOKENS.railTextDim,
            }}
          >
            {(fleetRow.summary &&
              (fleetRow.summary.policiesEvaluated ||
                fleetRow.policiesEvaluated)) || ''}
          </Box>
        )}
      </Box>
      {!isCollapsed &&
        hasChildren &&
        node.children.map((c) => (
          <OuTreeNode
            key={c.path}
            node={c}
            indent={indent + 1}
            filter={filter}
            collapsed={collapsed}
            toggleCollapse={toggleCollapse}
            selectedOuId={selectedOuId}
            selectedOuIds={selectedOuIds}
            multiSelectMode={multiSelectMode}
            onSelectOu={onSelectOu}
            onToggleOu={onToggleOu}
            fleetByOu={fleetByOu}
          />
        ))}
    </>
  );
}

// ── Workspace Overview ───────────────────────────────────────────────────

function WorkspaceOverview({ tenant, token, ous, fleetByOu, onOpenOu, onRefresh }) {
  // Tally
  const counts = useMemo(() => {
    let clean = 0;
    let drift = 0;
    let notAudited = 0;
    let totalDevices = 0;
    const rows = Object.values(fleetByOu || {});
    for (const r of rows) {
      if (r.status === 'compliant') clean += 1;
      else if (r.status === 'drift' || r.status === 'missing') drift += 1;
      else if (r.status === 'not-audited' || r.status === 'failed') notAudited += 1;
    }
    return { clean, drift, notAudited, totalOus: rows.length || ous.length, totalDevices };
  }, [fleetByOu, ous]);

  // Top problematic OUs — sort by severity * device count proxy
  const topProblematic = useMemo(() => {
    const rows = Object.values(fleetByOu || {})
      .filter((r) => r.status !== 'compliant' && r.status !== 'not-audited')
      .map((r) => ({
        ...r,
        score: (r.severity?.critical || 0) * 100 + (r.severity?.high || 0) * 10 + (r.driftCount || 0) + (r.missingCount || 0),
      }))
      .sort((a, b) => b.score - a.score)
      .slice(0, 8);
    return rows;
  }, [fleetByOu]);

  return (
    <>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          px: 4,
          py: 2,
          borderBottom: `1px solid ${TOKENS.line}`,
        }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            fontFamily: TOKENS.mono,
            fontSize: 13,
            color: TOKENS.inkMute,
          }}
        >
          <span>{tenant.displayName}</span>
          <span style={{ color: TOKENS.inkFade }}>/</span>
          <span style={{ color: TOKENS.ink, fontWeight: 500 }}>
            All OUs (workspace)
          </span>
        </Box>
        <Stack direction="row" spacing={1}>
          <Button
            variant="outlined"
            size="small"
            onClick={onRefresh}
            startIcon={<RefreshIcon />}
          >
            Refresh
          </Button>
        </Stack>
      </Box>

      <PageHeader
        title="Fleet at"
        emphasis="a glance"
        sub={`${counts.totalOus || 0} OUs · ${tenant.displayName || 'tenant'}`}
      />

      <Box sx={{ p: 3 }}>
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
            gap: 2,
            mb: 3,
          }}
        >
          <OverviewTile label="OUs" value={counts.totalOus} />
          <OverviewTile label="Audited & clean" value={counts.clean} tone="ok" />
          <OverviewTile label="Drift detected" value={counts.drift} tone="warn" />
          <OverviewTile label="Not audited" value={counts.notAudited} tone="bad" />
        </Box>

        <Paper variant="outlined" sx={{ p: 2 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1.5 }}>
            <Typography
              sx={{ fontFamily: TOKENS.sans, fontWeight: 600, fontSize: 16 }}
            >
              Top OUs needing attention
            </Typography>
            <Typography
              sx={{ fontFamily: TOKENS.mono, fontSize: 11, color: TOKENS.inkMute }}
            >
              sorted by severity × drift count
            </Typography>
          </Box>
          {topProblematic.length === 0 ? (
            <Alert severity="success">
              All audited OUs are currently compliant. Run a fresh sweep to be
              sure.
            </Alert>
          ) : (
            topProblematic.map((r) => (
              <Box
                key={r.ouId}
                onClick={() => onOpenOu(r.ouId)}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1.5,
                  py: 1.25,
                  borderBottom: `1px solid ${TOKENS.line2 || TOKENS.line}`,
                  cursor: 'pointer',
                  '&:hover': { background: TOKENS.bg },
                }}
              >
                <StatusDot status={ouStatusFromFleet(r)} size={8} />
                <Box component="span" sx={{ flex: 1, fontSize: 13 }}>
                  {r.ouPath}
                </Box>
                <Box
                  component="span"
                  sx={{
                    fontFamily: TOKENS.mono,
                    fontSize: 11,
                    color: TOKENS.inkMute,
                  }}
                >
                  drift {r.driftCount || 0} · miss {r.missingCount || 0}
                </Box>
                <Chip
                  size="small"
                  label={`${r.templateName} v${r.templateVersion || '?'}`}
                  variant="outlined"
                  sx={{ fontFamily: TOKENS.mono, fontSize: 10 }}
                />
              </Box>
            ))
          )}
        </Paper>
      </Box>
    </>
  );
}

function OverviewTile({ label, value, tone, subline }) {
  const toneColors = {
    ok: TOKENS.ok,
    warn: TOKENS.warn,
    bad: TOKENS.bad,
  };
  const accent = toneColors[tone] || TOKENS.ink;
  return (
    <Paper
      variant="outlined"
      sx={{
        p: 2,
        borderColor: TOKENS.hairline,
        borderLeft: `3px solid ${accent}`,
      }}
    >
      <Typography
        sx={{
          fontFamily: TOKENS.mono,
          fontSize: 10,
          letterSpacing: '0.16em',
          textTransform: 'uppercase',
          color: TOKENS.inkMute,
        }}
      >
        {label}
      </Typography>
      <Typography
        sx={{
          fontFamily: TOKENS.mono,
          fontSize: 34,
          fontWeight: 600,
          color: accent,
          lineHeight: 1,
          mt: 0.5,
        }}
      >
        {value}
      </Typography>
      {subline && (
        <Typography sx={{ fontSize: 12, color: TOKENS.inkMute, mt: 0.5 }}>
          {subline}
        </Typography>
      )}
    </Paper>
  );
}

// ── OU Detail page (Compliance · Devices · History · Policy Inspector) ──

function OuDetailPage({
  token,
  tenant,
  ou,
  fleetRow,
  assignmentRow,
  subTab,
  onSelectSubTab,
  onAuditDone,
}) {
  const [running, setRunning] = useState(false);
  const [runError, setRunError] = useState(null);

  const runAudit = useCallback(async () => {
    const templateName =
      (assignmentRow && assignmentRow.templateName) ||
      (fleetRow && fleetRow.templateName) ||
      'correctional-kiosk';
    setRunning(true);
    setRunError(null);
    try {
      const resp = await fetch('/api/google-ou-compliance/audits/run', {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          templateName,
          ouId: ou.id,
          ouDisplayName: ou.path,
          generateAiExplanations: false,
        }),
      });
      if (!resp.ok) {
        const data = await resp.json().catch(() => ({}));
        throw new Error(
          data.message || `Audit failed (HTTP ${resp.status})`,
        );
      }
      await resp.json();
      onAuditDone && onAuditDone();
    } catch (e) {
      setRunError(e.message || String(e));
    } finally {
      setRunning(false);
    }
  }, [token, ou, assignmentRow, fleetRow, onAuditDone]);

  return (
    <>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          px: 4,
          py: 2,
          borderBottom: `1px solid ${TOKENS.line}`,
        }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            fontFamily: TOKENS.mono,
            fontSize: 13,
            color: TOKENS.inkMute,
            overflow: 'hidden',
          }}
        >
          <span>{tenant.displayName}</span>
          <span style={{ color: TOKENS.inkFade }}>/</span>
          <span style={{ color: TOKENS.ink, fontWeight: 500 }}>{ou.path}</span>
        </Box>
        <Stack direction="row" spacing={1}>
          <Button
            variant="outlined"
            size="small"
            disabled={running}
            onClick={runAudit}
            startIcon={
              running ? <CircularProgress size={14} /> : <RunIcon />
            }
          >
            Re-run audit
          </Button>
        </Stack>
      </Box>

      <PageHeader
        title={ou.name}
        emphasis=""
        sub={
          fleetRow
            ? `Last audited ${fleetRow.auditedAt ? new Date(fleetRow.auditedAt).toLocaleString() : '—'} · template ${fleetRow.templateName || '—'}${fleetRow.templateVersion ? ' v' + fleetRow.templateVersion : ''}`
            : 'Never audited from this OU. Click Re-run audit to start.'
        }
      />

      <Box sx={{ display: 'flex', borderBottom: `1px solid ${TOKENS.line}`, px: 4 }}>
        {[
          { id: 'compliance', label: 'Compliance' },
          { id: 'devices', label: 'Devices' },
          { id: 'history', label: 'History' },
          { id: 'inspector', label: 'Policy inspector' },
        ].map((t) => {
          const active = subTab === t.id;
          return (
            <Box
              key={t.id}
              onClick={() => onSelectSubTab(t.id)}
              sx={{
                py: 1.75,
                mr: 3.5,
                fontSize: 13,
                fontWeight: 500,
                color: active ? TOKENS.ink : TOKENS.inkMute,
                borderBottom: `2px solid ${active ? TOKENS.accent : 'transparent'}`,
                cursor: 'pointer',
              }}
            >
              {t.label}
            </Box>
          );
        })}
      </Box>

      <Box sx={{ p: 3 }}>
        {runError && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {runError}
          </Alert>
        )}

        {subTab === 'compliance' && (
          <OuCompliancePanel
            token={token}
            ou={ou}
            fleetRow={fleetRow}
          />
        )}
        {subTab === 'devices' && (
          <OuDeviceDrillDown
            token={token}
            ouPath={ou.path}
            ouId={ou.id}
            open
            onClose={() => onSelectSubTab('compliance')}
            agg={fleetRow}
          />
        )}
        {subTab === 'history' && (
          <OuHistoryPanel token={token} ouId={ou.id} ouPath={ou.path} />
        )}
        {subTab === 'inspector' && (
          <PolicyInspectorPanel token={token} ou={ou} />
        )}
      </Box>
    </>
  );
}

function OuCompliancePanel({ token, ou, fleetRow }) {
  if (!fleetRow || !fleetRow.auditId) {
    return (
      <Alert severity="info">
        No audit yet. Click <strong>Re-run audit</strong> to start. The chosen
        template is the assigned (or heuristic) one — change via Tenant tab.
      </Alert>
    );
  }
  return (
    <AuditDetail
      token={token}
      auditId={fleetRow.auditId}
      onBack={() => {}}
    />
  );
}

function OuHistoryPanel({ token, ouId, ouPath }) {
  const [state, setState] = useState({ loading: true, audits: [], error: null });
  useEffect(() => {
    let active = true;
    fetch('/api/google-ou-compliance/audits?limit=100', {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then((r) => r.json())
      .then((d) => {
        if (!active) return;
        const filtered = (d.data || []).filter(
          (a) => a.ouId === ouId || a.ouId === ouPath,
        );
        setState({ loading: false, audits: filtered, error: null });
      })
      .catch((e) =>
        setState({ loading: false, audits: [], error: e.message }),
      );
    return () => {
      active = false;
    };
  }, [token, ouId, ouPath]);

  if (state.loading) return <CircularProgress />;
  if (state.error)
    return <Alert severity="error">{state.error}</Alert>;
  if (state.audits.length === 0)
    return <Alert severity="info">No audit history for this OU.</Alert>;

  return (
    <Paper variant="outlined">
      <Box
        component="table"
        sx={{
          width: '100%',
          borderCollapse: 'collapse',
          fontSize: 13,
          '& th, & td': {
            textAlign: 'left',
            px: 2,
            py: 1.25,
            borderBottom: `1px solid ${TOKENS.line}`,
          },
          '& th': {
            background: TOKENS.bg,
            fontWeight: 600,
            color: TOKENS.ink,
            fontFamily: TOKENS.mono,
            fontSize: 11,
            letterSpacing: '0.06em',
            textTransform: 'uppercase',
          },
          '& tbody tr:hover': { background: TOKENS.bg },
        }}
      >
        <thead>
          <tr>
            <th>When</th>
            <th>Template</th>
            <th>Result</th>
            <th>Triggered by</th>
            <th></th>
          </tr>
        </thead>
        <tbody>
          {state.audits.map((a) => (
            <tr key={a.id}>
              <td style={{ fontFamily: TOKENS.mono, fontSize: 12 }}>
                {a.completedAt
                  ? new Date(a.completedAt).toLocaleString()
                  : new Date(a.startedAt).toLocaleString()}
              </td>
              <td style={{ fontFamily: TOKENS.mono, fontSize: 12 }}>
                {a.templateName} · v{a.templateVersion}
              </td>
              <td>
                {a.summaryStats?.matchCount ?? '—'} match · {a.summaryStats?.driftCount ?? 0} drift · {a.summaryStats?.missingCount ?? 0} miss
              </td>
              <td style={{ fontSize: 12, color: TOKENS.inkMute }}>
                {a.notes && a.notes.includes('[bulk job') ? 'scheduler' : 'user'}
              </td>
              <td>
                <Chip
                  size="small"
                  label={a.status}
                  color={a.status === 'completed' ? 'success' : 'default'}
                />
              </td>
            </tr>
          ))}
        </tbody>
      </Box>
    </Paper>
  );
}

function PolicyInspectorPanel({ token, ou }) {
  return (
    <Alert severity="info">
      Policy Inspector — drill into raw resolved policies for this OU.
      Reuses the same Chrome Policy API response the audit pipeline parses.
      Wire-up follows in a Phase B+ patch; for now the History tab links to
      each audit's policy detail (which contains the same data).
    </Alert>
  );
}

// ── Multi-OU Compare ─────────────────────────────────────────────────────

function MultiOuCompare({ token, ous, selectedIds, fleetByOu, onClear }) {
  const selected = useMemo(
    () => ous.filter((o) => selectedIds.includes(o.id)),
    [ous, selectedIds],
  );
  if (selected.length === 0) {
    return (
      <Box sx={{ p: 4 }}>
        <Alert severity="info">
          Multi-select mode is on but no OUs picked. Tick checkboxes in the rail.
        </Alert>
      </Box>
    );
  }

  return (
    <>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          px: 4,
          py: 2,
          borderBottom: `1px solid ${TOKENS.line}`,
        }}
      >
        <Box sx={{ fontFamily: TOKENS.mono, fontSize: 13, color: TOKENS.inkMute }}>
          Compare · {selected.length} OUs
        </Box>
        <Button onClick={onClear} startIcon={<BackIcon />} size="small">
          Clear selection
        </Button>
      </Box>

      <PageHeader
        title="Compare"
        emphasis={`${selected.length} OUs`}
        sub={selected.map((o) => o.path).join(' · ')}
      />

      <Box sx={{ p: 3 }}>
        <Paper
          variant="outlined"
          sx={{ overflowX: 'auto', borderColor: TOKENS.hairline }}
        >
          <Box
            component="table"
            sx={{
              width: '100%',
              borderCollapse: 'collapse',
              fontSize: 13,
              '& th, & td': {
                borderBottom: `1px solid ${TOKENS.line}`,
                px: 2,
                py: 1.5,
                textAlign: 'left',
              },
              '& th': {
                background: TOKENS.bg,
                fontFamily: TOKENS.mono,
                fontSize: 10,
                letterSpacing: '0.06em',
                textTransform: 'uppercase',
              },
            }}
          >
            <thead>
              <tr>
                <th>Metric</th>
                {selected.map((o) => (
                  <th key={o.id}>{o.name}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              <CompareRow label="Devices" selected={selected} fleetByOu={fleetByOu} keyName="deviceCount" />
              <CompareRow label="Drift" selected={selected} fleetByOu={fleetByOu} keyName="driftCount" />
              <CompareRow label="Missing" selected={selected} fleetByOu={fleetByOu} keyName="missingCount" />
              <CompareRow label="Match" selected={selected} fleetByOu={fleetByOu} keyName="matchCount" />
              <CompareRow label="Template" selected={selected} fleetByOu={fleetByOu} keyName="templateName" />
            </tbody>
          </Box>
        </Paper>
      </Box>
    </>
  );
}

function CompareRow({ label, selected, fleetByOu, keyName }) {
  return (
    <tr>
      <td style={{ fontFamily: 'inherit', fontWeight: 500 }}>{label}</td>
      {selected.map((o) => {
        const r = fleetByOu[o.id];
        const v = r ? r[keyName] ?? (r.summary && r.summary[keyName]) : '—';
        return (
          <td
            key={o.id}
            style={{
              fontFamily: TOKENS.mono,
              fontSize: 18,
              fontWeight: 600,
            }}
          >
            {v === undefined || v === null ? '—' : String(v)}
          </td>
        );
      })}
    </tr>
  );
}

// ── Audit Log (read-only) ────────────────────────────────────────────────

function AuditLogPage({ token }) {
  const [state, setState] = useState({ loading: true, audits: [], error: null });
  useEffect(() => {
    fetch('/api/google-ou-compliance/audits?limit=200', {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then((r) => r.json())
      .then((d) =>
        setState({ loading: false, audits: d.data || [], error: null }),
      )
      .catch((e) =>
        setState({ loading: false, audits: [], error: e.message }),
      );
  }, [token]);

  return (
    <>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          px: 4,
          py: 2,
          borderBottom: `1px solid ${TOKENS.line}`,
        }}
      >
        <Box sx={{ fontFamily: TOKENS.mono, fontSize: 13, color: TOKENS.inkMute }}>
          Audit Log
        </Box>
      </Box>
      <PageHeader
        title="Audit"
        emphasis="Log"
        sub={`All runs · ${state.audits.length} audits`}
      />
      <Box sx={{ p: 3 }}>
        {state.loading && <CircularProgress />}
        {state.error && <Alert severity="error">{state.error}</Alert>}
        {!state.loading && state.audits.length === 0 && (
          <Alert severity="info">No audits yet.</Alert>
        )}
        {!state.loading && state.audits.length > 0 && (
          <Paper variant="outlined">
            <Box
              component="table"
              sx={{
                width: '100%',
                borderCollapse: 'collapse',
                fontSize: 13,
                '& th, & td': {
                  borderBottom: `1px solid ${TOKENS.line}`,
                  px: 2,
                  py: 1.25,
                  textAlign: 'left',
                },
                '& th': {
                  background: TOKENS.bg,
                  fontFamily: TOKENS.mono,
                  fontSize: 10,
                  letterSpacing: '0.06em',
                  textTransform: 'uppercase',
                },
                '& tbody tr:hover': { background: TOKENS.bg },
              }}
            >
              <thead>
                <tr>
                  <th>When</th>
                  <th>OU</th>
                  <th>Template</th>
                  <th>Summary</th>
                  <th>Source</th>
                </tr>
              </thead>
              <tbody>
                {state.audits.map((a) => (
                  <tr key={a.id}>
                    <td style={{ fontFamily: TOKENS.mono, fontSize: 12 }}>
                      {a.completedAt
                        ? new Date(a.completedAt).toLocaleString()
                        : new Date(a.startedAt).toLocaleString()}
                    </td>
                    <td>{a.ouDisplayName || a.ouId}</td>
                    <td style={{ fontFamily: TOKENS.mono, fontSize: 12 }}>
                      {a.templateName} · v{a.templateVersion}
                    </td>
                    <td>
                      {a.summaryStats?.matchCount ?? '—'} match ·{' '}
                      {a.summaryStats?.driftCount ?? 0} drift ·{' '}
                      {a.summaryStats?.missingCount ?? 0} miss
                    </td>
                    <td>
                      <Chip size="small" label={a.source} variant="outlined" />
                    </td>
                  </tr>
                ))}
              </tbody>
            </Box>
          </Paper>
        )}
      </Box>
    </>
  );
}
